import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'antd';
import styles from './PhoneReportModal.styles';
import { Upload, Button } from 'antd';
import Papa from 'papaparse';
import { CSVLink } from 'react-csv';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { getIsVisibleModal, closeModal } from '../../redux/store/ui/common';

const totalRowsHeader = [
  'callId',
  'line',
  'date',
  'time',
  'numberTimezone',
  'datetime (UTC)',
  'direction',
  'from',
  'to',
  'anweredAircall',
  'anweredVanad',
  'missed_call_reason',
  'agent',
  'duration (total)',
  'duration (in call)',
  'waitingtimeVanad',
  'talkTimeVanad',
  'holdTimeVanad',
  'afterCallWorkVanad',
  'handlingTimeVanad',
  'via',
  'voicemail',
  'recording',
  'comments',
  'tags',
  'call quality',
  'teams',
];

type PhoneReportModalProps = {
  visible?: boolean;
};

const PhoneReportModal: React.FC<PhoneReportModalProps> = ({ visible }) => {
  const dispatch = useDispatch();
  const _visible = useSelector(getIsVisibleModal('phoneReportsModal'));
  const [key, setKey] = React.useState(new Date().toString());
  const [airCallRow, setAirCallRow] = React.useState(null) as any;
  const [vanadRow, setVanadRow] = React.useState(null) as any;
  const [totalRows, setTotalRows] = React.useState([totalRowsHeader]);

  const handleCancel = () => {
    dispatch(closeModal('phoneReportsModal'));
  };

  useEffect(() => {
    setKey(new Date().toString());
    setAirCallRow(null);
    setVanadRow(null);
    setTotalRows([totalRowsHeader]);
  }, [_visible]);

  const airCallProps: any = {
    name: 'file',
    accept: '.csv',
    maxCount: 1,
    beforeUpload: (file) => {
      Papa.parse(file, {
        complete: (results) => setAirCallRow(results),
        error(error) {
          if (error) {
            setAirCallRow(null);
          }
        },
      });
      return false;
    },
    onRemove: () => {
      setAirCallRow(null);
    },
  };

  const vanadProps: any = {
    name: 'file',
    accept: '.csv',
    maxCount: 1,
    beforeUpload: (file) => {
      Papa.parse(file, {
        complete: (results) => setVanadRow(results),
        error(error) {
          if (error) {
            setVanadRow(null);
          }
        },
      });
      return false;
    },
    onRemove: () => {
      setVanadRow(null);
    },
  };

  // Flexible lookup supporting case-insensitive and alias matching
  const getValueByAliases = (
    item: { [x: string]: any },
    titles: string[] = [],
    aliases: string[]
  ) => {
    if (!titles || !aliases) return '';
    const norm = (s: string) => (s || '').toString().trim().toLowerCase();
    const lowerTitles = titles.map(norm);
    for (const a of aliases) {
      const idx = lowerTitles.indexOf(norm(a));
      if (idx >= 0) return item[idx] ?? '';
    }
    return '';
  };

  const getIndexOf = (
    item: { [x: string]: any },
    titles: string[] = [],
    title: string
  ) => getValueByAliases(item, titles, [title]);

  const onDownloadPress = (event: any, done: () => void) => {
    let air: any = [];
    let vanad: any = [];
    const airCallTitles = airCallRow?.data?.[0];
    const vanadTitles = vanadRow?.data?.[0];

    // Aliases for Aircall headers (case-insensitive) mapped to unified columns
    const AIRCALL_ALIASES = {
      callId: ['Call id', 'call id', 'Call ID', 'Call id (internal)'],
      line: ['line', 'Aircall number'],
      date: ['date (TZ offset incl.)', 'date'],
      time: ['time (TZ offset incl.)', 'time'],
      datetime: ['datetime (tz offset incl.)', 'datetime (TZ offset incl.)'],
      numberTimezone: ['number timezone', 'number_timezone'],
      datetimeUtc: ['datetime (UTC)', 'datetime (utc)', 'Call start time'],
      direction: ['direction', 'Call direction - type'],
      from: ['from', 'Customer number'],
      to: ['to', 'Aircall number'],
      answered: ['answered', 'Answered'],
      missedReason: ['missed_call_reason'],
      user: ['user', 'agent'],
      durationTotal: ['duration (total)'],
      durationInCall: ['duration (in call)', 'In-call duration'],
      via: ['via'],
      voicemail: ['voicemail'],
      recording: ['Recording', 'recording', 'Play recording/voicemail'],
      comments: ['comments'],
      tags: ['tags'],
      callQuality: ['Call quality', 'call quality'],
      teams: ['team', 'teams'],
    } as const;

    // Aliases for Vanad headers (case-insensitive) mapped to unified columns
    const VANAD_ALIASES = {
      callId: ['Call ID', 'Call id'],
      line: ['Queue', 'Line', 'Team'],
      date: ['Date'],
      time: ['Time'],
      // numberTimezone intentionally empty
      answered: ['Answered'],
      agent: ['Agent', 'User'],
      waitingTime: ['Waiting Time'],
      talkTime: ['Talk Time'],
      holdTime: ['Hold Time'],
      afterCallWork: ['After Call Work'],
      handlingTime: ['Handling Time'],
      recording: ['Link to voice recording', 'Recording'],
    } as const;

    const buildRow = (
      source: 'aircall' | 'vanad',
      item: { [x: string]: any },
      titles: string[]
    ) => {
      const getA = (key: keyof typeof AIRCALL_ALIASES) =>
        getValueByAliases(item, titles, AIRCALL_ALIASES[key]);
      const getV = (key: keyof typeof VANAD_ALIASES) =>
        getValueByAliases(item, titles, VANAD_ALIASES[key]);

      const isAir = source === 'aircall';
      const values: any[] = [];
      totalRowsHeader.forEach((header) => {
        switch (header) {
          case 'callId':
            values.push(isAir ? getA('callId') : getV('callId'));
            break;
          case 'line':
            values.push(isAir ? getA('line') : getV('line'));
            break;
          case 'date':
            if (isAir) {
              // Try combined datetime first, fallback to separate date field
              const dt = getA('datetime');
              if (dt) {
                values.push(dt.split(' ')[0]);
              } else {
                values.push(getA('date'));
              }
            } else {
              values.push(getV('date'));
            }
            break;
          case 'time':
            if (isAir) {
              // Try combined datetime first, fallback to separate time field
              const dt = getA('datetime');
              if (dt) {
                values.push(dt.split(' ')[1]);
              } else {
                values.push(getA('time'));
              }
            } else {
              values.push(getV('time'));
            }
            break;
          case 'numberTimezone':
            values.push(isAir ? getA('numberTimezone') : '');
            break;
          case 'datetime (UTC)': {
            if (isAir) {
              // Try UTC datetime field first
              const dtUtc = getA('datetimeUtc');
              if (dtUtc) {
                values.push(new Date(dtUtc));
              } else {
                // Try combined datetime field
                const dt = getA('datetime');
                if (dt) {
                  values.push(new Date(dt));
                } else {
                  // Fallback to separate date/time fields
                  const d = getA('date');
                  const t = getA('time');
                  values.push(new Date(`${d} ${t}`));
                }
              }
            } else {
              const d = getV('date');
              const t = getV('time');
              values.push(new Date(`${d} ${t}`));
            }
            break;
          }
          case 'direction':
            values.push(isAir ? getA('direction') : '');
            break;
          case 'from':
            values.push(isAir ? getA('from') : '');
            break;
          case 'to':
            values.push(isAir ? getA('to') : '');
            break;
          case 'anweredAircall':
            values.push(isAir ? getA('answered') : '');
            break;
          case 'anweredVanad':
            values.push(isAir ? '' : getV('answered'));
            break;
          case 'missed_call_reason':
            values.push(isAir ? getA('missedReason') : '');
            break;
          case 'agent':
            values.push(isAir ? getA('user') : getV('agent'));
            break;
          case 'duration (total)':
            values.push(isAir ? getA('durationTotal') : 0);
            break;
          case 'duration (in call)':
            values.push(isAir ? getA('durationInCall') : 0);
            break;
          case 'waitingtimeVanad':
            values.push(isAir ? 0 : getV('waitingTime'));
            break;
          case 'talkTimeVanad':
            values.push(isAir ? 0 : getV('talkTime'));
            break;
          case 'holdTimeVanad':
            values.push(isAir ? 0 : getV('holdTime'));
            break;
          case 'afterCallWorkVanad':
            values.push(isAir ? 0 : getV('afterCallWork'));
            break;
          case 'handlingTimeVanad':
            values.push(isAir ? 0 : getV('handlingTime'));
            break;
          case 'via':
            values.push(isAir ? getA('via') : '');
            break;
          case 'voicemail':
            values.push(isAir ? getA('voicemail') : '');
            break;
          case 'recording':
            values.push(isAir ? getA('recording') : getV('recording'));
            break;
          case 'comments':
            values.push(isAir ? getA('comments') : '');
            break;
          case 'tags':
            values.push(isAir ? getA('tags') : '');
            break;
          case 'call quality':
            values.push(isAir ? getA('callQuality') : '');
            break;
          case 'teams':
            values.push(isAir ? getA('teams') : '');
            break;
          default:
            values.push('');
        }
      });
      return values;
    };

    if (airCallRow?.data?.length > 0) {
      airCallRow?.data?.forEach((item: { [x: string]: any }, i: number) => {
        if (i > 0) {
          air.push(buildRow('aircall', item, airCallTitles));
        }
      });
    }
    if (vanadRow?.data?.length > 0) {
      vanadRow?.data?.forEach((item: { [x: string]: any }, i: number) => {
        if (i > 0) {
          vanad.push(buildRow('vanad', item, vanadTitles));
        }
      });
    }
    const toSort = [...air, ...vanad];
    toSort.sort(function (a, b) {
      // @ts-ignore
      return new Date(a[5]) - new Date(b[5]);
    });
    setTotalRows([...totalRows, ...toSort]);

    setTimeout(() => {
      setKey(new Date().toString());
      setAirCallRow(null);
      setVanadRow(null);
      setTotalRows([totalRowsHeader]);
    }, 200);
  };

  return (
    <>
      <div>
        <Modal
          wrapClassName="add-service-wrapper"
          title={`Phone reports`}
          visible={visible || _visible}
          width={720}
          onCancel={handleCancel}
          footer={null}
        >
          <div style={{ marginTop: '16px', marginBottom: '16px' }}>
            <Upload key={`${key}-airCallRow`} {...airCallProps}>
              <Button icon={<UploadOutlined />}>Upload AirCall Report</Button>
            </Upload>
          </div>
          <div style={{ marginTop: '16px', marginBottom: '16px' }}>
            <Upload key={`${key}-vanadRow`} {...vanadProps}>
              <Button icon={<UploadOutlined />}>Upload Vanad Report</Button>
            </Upload>
          </div>
          <Button
            disabled={!airCallRow && !vanadRow}
            type="primary"
            icon={<DownloadOutlined />}
          >
            <CSVLink
              className={
                !airCallRow && !vanadRow
                  ? 'disabled-download-btn'
                  : 'download-btn'
              }
              filename="total-phone-report.csv"
              data={totalRows}
              asyncOnClick
              onClick={onDownloadPress}
            >
              Merge and download report
            </CSVLink>
          </Button>
        </Modal>
      </div>
      <style jsx global>
        {styles}
      </style>
    </>
  );
};

export default PhoneReportModal;
